import { FunctionComponent, useState } from "react";
import { format, subDays } from "date-fns";
import { useQuery } from "@apollo/client";
import { useAuth } from "../../context/AuthContext";
import SkeletonLoader from "../common/SkeletonLoader";
import { GET_MEETINGS } from "../../api/query";
import { useNavigate } from "react-router-dom";

export type ActionRowType = {
    imageurl?: string;
    heading?: string;
};

const TodayMeetingsRow: FunctionComponent<ActionRowType> = ({
    imageurl,
    heading,
}) => {

    const navigate = useNavigate()

    const { userInfo } = useAuth()
    const employeeID = userInfo?.employee_id || '';
    const agencyId = userInfo?.agencies?.id

    const [currentPage, setCurrentPage] = useState(0); // pagination state
    const itemsPerPage = 3; // 3 tasks per page

    const now = new Date();
    const startDate = format(now, "yyyy-MM-dd'T'00:00:00'Z'");
    const endDate = format(now, "yyyy-MM-dd'T'23:59:59'Z'");

    //api
    const {
        loading,
        data: meetingsData,
    } = useQuery(GET_MEETINGS, {
        variables: {
            start_date: startDate,
            end_date: endDate,
            agency_id: Number(agencyId),
            employee_id: parseInt(employeeID)
        },
        fetchPolicy: "no-cache",
        skip: !agencyId,
    });

    const getMeetings = meetingsData?.get_meetings?.meetings?.filter(
        (meeting: any) => meeting.is_deleted === false
    );

    if (loading) {
        return (
            <div
                className="self-stretch shadow-[0px_1px_4px_rgba(0,_0,_0,_0.25)] mobile-action-card-blg-dv rounded-lg bg-surface-bg-primary flex flex-col items-start justify-start p-4 box-border gap-[6px] max-w-full text-left text-base text-colors-text-black font-body-xs-semi-bold "
            >
                <div className="self-stretch flex flex-row items-center justify-start gap-[16px] max-w-full mq950:flex-wrap inner-mobile-action-card-blg-dv">
                    <div className="flex-1 flex flex-row items-start justify-start gap-[6px] max-w-full">
                        <h5 className="relative font-bold text-xl-dv mt-0 mb-[16px] capitalize dark:text-white">{heading}</h5>
                    </div>
                </div>
                <div className="w-full flex flex-col gap-4">
                    <div className="flex justify-between items-center">
                        <SkeletonLoader count={1} height="h-4" width="w-1/3" />
                        <SkeletonLoader count={1} height="h-6" width="w-16" borderRadius="rounded-full" />
                    </div>
                    <div className="flex gap-4 items-center">
                        <SkeletonLoader count={1} height="h-3" width="w-24" />
                        <SkeletonLoader count={1} height="h-3" width="w-32" />
                    </div>
                    <div className="flex justify-between items-center">
                        <SkeletonLoader count={1} height="h-4" width="w-2/5" />
                        <SkeletonLoader count={1} height="h-6" width="w-16" borderRadius="rounded-full" />
                    </div>
                    <div className="flex gap-4 items-center">
                        <SkeletonLoader count={1} height="h-3" width="w-28" />
                        <SkeletonLoader count={1} height="h-3" width="w-24" />
                    </div>
                    <div className="flex justify-between items-center">
                        <SkeletonLoader count={1} height="h-4" width="w-1/3" />
                        <SkeletonLoader count={1} height="h-6" width="w-16" borderRadius="rounded-full" />
                    </div>
                    <div className="flex gap-4 items-center">
                        <SkeletonLoader count={1} height="h-3" width="w-20" />
                        <SkeletonLoader count={1} height="h-3" width="w-28" />
                    </div>
                </div>
            </div>
        )
    }

    // Pagination calculations
    const totalPages = Math.ceil(getMeetings?.length / itemsPerPage);
    const startIndex = currentPage * itemsPerPage;
    const visibleMeetings = getMeetings?.slice(startIndex, startIndex + itemsPerPage);

    return (
        <div
            className="self-stretch shadow-[0px_1px_4px_rgba(0,_0,_0,_0.25)] mobile-action-card-blg-dv rounded-lg bg-surface-bg-primary flex flex-col items-start justify-start p-4 box-border gap-[6px] max-w-full text-left text-base text-colors-text-black font-body-xs-semi-bold "
        >
            <div className="self-stretch flex flex-row items-center justify-start gap-[16px] max-w-full mq950:flex-wrap inner-mobile-action-card-blg-dv">
                <div className="flex-1 flex flex-row items-start justify-start gap-[6px] max-w-full">

                    <h5 className="relative font-bold text-xl-dv mt-0 mb-[16px] capitalize dark:text-white">{`${heading} (${getMeetings?.length ?? 0})`}</h5>
                </div>
            </div>
            <div className={`max-w-full flex min-w-full max-h-[284px] overflow-x-hidden overflow-y-auto`}>
                <div className="grid grid-cols-3 mq1900:grid-cols-3 mq1425:grid-cols-2 mq1275:grid-cols-2 mq950:grid-cols-2 mq825:grid-cols-1 mq575:grid-cols-1 min-w-full  gap-[24px]">
                    {visibleMeetings?.map((meeting: any) => {
                        const meetingMembers = [
                            ...(meeting?.members || []),
                            ...(meeting?.external_members || []),
                        ];

                        const meetingMembersLength = meetingMembers?.length;

                        const meetingEnd = new Date(meeting?.end_datetime);
                        // const isPastMeeting = meetingEnd < new Date();

                        // Find current user in members
                        const currentMember = meeting?.members?.find(
                            (m: any) => String(m.employee_id) === String(employeeID)
                        );

                        let statusLabel = "";
                        let statusClass = "";

                        if (meeting?.is_email_send == true) {
                            if (currentMember?.is_attended) {
                                statusLabel = "Attended";
                                statusClass = "bg-green-100 border-green-500 text-green-700";
                            } else {
                                statusLabel = "Missed";
                                statusClass = "bg-red-100 border-red-500 text-red-700";
                            }
                        }

                        return (
                            <div className="customtask_card_ui_dv  w-full relative rounded cursor-pointer bg-white border-whitesmoke border-solid border-[1px] box-border  flex items-start justify-start pt-4 px-4 pb-4 text-left text-base text-gray font-manrope"
                                key={meeting?.id}
                            // onClick={() => {
                            //     navigate("/task_detail", {
                            //         state: {
                            //             taskId: task?.id,
                            //         }
                            //     })
                            // }}
                            >
                                <div className=" w-full relative rounded cursor-pointer   shrink-0 block flex-col items-start justify-start text-left text-base text-gray font-manrope">
                                    <div className="self-stretch flex flex-col items-start justify-start">
                                        <div className="self-stretch flex flex-row items-center justify-start">
                                            <div className="flex-1 flex flex-row items-center justify-start pb-2">
                                                <p className="relative font-semibold text-sm my-0 capitalize tasktitle_card">{meeting?.title}</p>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="grid gap-2 w-full">

                                        <div className="ctheight_blg_dv">
                                            <hr className="bdr-custom-taskcrad" />
                                            <div className="self-stretch flex flex-col items-start justify-center gap-2 text-dimgray">
                                                <div className="self-stretch flex flex-row items-start justify-start">
                                                    <p className="flex-1 relative dark:text-white my-0">{meeting?.meeting_type_name}</p>
                                                </div>
                                                <hr className="bdr-custom-taskcrad" />
                                                <div className="self-stretch flex flex-row items-start justify-start gap-2">
                                                    <p className="w-[120px] relative inline-block shrink-0 dark:text-white my-0">Time:</p>
                                                    <p className="relative font-semibold text-gray dark:text-white my-0">
                                                        {`${format(
                                                            new Date(meeting.start_datetime),
                                                            "hh:mm a"
                                                        )} - ${format(
                                                            new Date(meeting.end_datetime),
                                                            "hh:mm a"
                                                        )}`}</p>
                                                </div>
                                                <div className="self-stretch flex flex-row items-start justify-start gap-2">
                                                    <p className="w-[120px] relative inline-block shrink-0 dark:text-white my-0">Members:</p>
                                                    {/* Wrapper for hover tooltip */}
                                                    <div className="relative group">
                                                        <p className="relative font-semibold text-gray dark:text-white my-0 cursor-pointer">
                                                            {meetingMembersLength}
                                                        </p>

                                                        {/* Tooltip */}
                                                        <div className="absolute top-8 left-0 hidden group-hover:block bg-white shadow-lg rounded-lg p-2 w-48 z-50">
                                                            <ul className="text-sm text-gray-700 max-h-40 overflow-y-auto">
                                                                {meetingMembers?.map((member: any, i: number) => (
                                                                    <li
                                                                        key={i}
                                                                        className="py-1 px-2 hover:bg-gray-100 rounded dark:text-white"
                                                                    >
                                                                        {member?.member_name || member?.name || `Member ${i + 1}`}
                                                                    </li>
                                                                ))}
                                                            </ul>
                                                        </div>
                                                    </div>
                                                    {/* <p className="relative font-semibold text-gray dark:text-white my-0">{meetingMembersLength}</p> */}
                                                </div>
                                                {meeting?.meeting_type_id == 7 && (
                                                    <>
                                                        <div className="self-stretch flex flex-row items-start justify-start gap-2">
                                                            <p className="w-[120px] relative inline-block shrink-0 dark:text-white my-0">Project Name:</p>
                                                            <p className="relative font-semibold text-gray dark:text-white my-0">{meeting?.project_name}</p>
                                                        </div>
                                                        <div className="self-stretch flex flex-row items-start justify-start gap-2">
                                                            <p className="w-[120px] relative inline-block shrink-0 dark:text-white my-0">Goal Name:</p>
                                                            <p className="relative font-semibold text-gray dark:text-white my-0">{meeting?.goal_name}</p>
                                                        </div>
                                                    </>
                                                )}
                                            </div>
                                            {/* <hr className="bdr-custom-taskcrad" /> */}

                                        </div>
                                        <div className="self-stretch flex flex-row items-start justify-between gap-0 mt-5">
                                            {(meeting?.is_email_send == true) && (
                                                <div className={`rounded-[50px] attendedbg-blg   flex flex-row items-center justify-center py-1 px-4 ${statusClass}`}>
                                                    <p className="relative dark:text-white my-0">{statusLabel}</p>
                                                </div>
                                            )}

                                            {!(meeting?.is_email_send == true) && (
                                                // <div className="rounded-[100px] bg-crimson right_status_clockout flex flex-row items-center justify-center py-1 px-2 text-sm text-white">
                                                <div
                                                    className="rounded-[50px] joinnow_bg bg-[#f2f2f2] border-solid border-[1px] flex flex-row items-center justify-center py-1 px-4"
                                                    onClick={() => window.open(meeting?.meeting_link_string, "_blank")}
                                                >
                                                    Join Now
                                                </div>
                                                // </div>
                                            )}

                                            <div
                                                onClick={() => {
                                                    navigate("/meeting_detail", {
                                                        state: {
                                                            meetingID: meeting?.id,
                                                            startMeetDate: meeting?.start_datetime,
                                                            endMeetDate: meeting?.end_datetime
                                                        }
                                                    })
                                                }}
                                                className="rounded-[100px] viewbg-dv bg-crimson right_status_clockout flex flex-row items-center justify-center py-1 px-2 text-sm text-white">
                                                <div className="relative font-semibold">View</div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )
                    })}
                    {visibleMeetings?.length === 0 && (
                        <>No Meetings Available</>
                    )}
                    {
                        meetingsData?.get_meetings?.meetings?.status === "Failure" && (
                            <div className="border-[1px] border-solid border-surface-border min-w-full bg-white flex justify-center items-center action-nodeta_card">
                                <span className="text-left text-sm text-text-text-secondary"> No data available</span>
                            </div>
                        )
                    }
                </div>
            </div>
            {/* Pagination dots */}
            {totalPages > 1 && (
                <div className="flex justify-center items-center mt-4 gap-2 pagination-action-blg">
                    {Array.from({ length: totalPages }).map((_, index) => (
                        <button
                            key={index}
                            onClick={() => setCurrentPage(index)}
                            className={`h-3 w-3 rounded-full transition-all ${index === currentPage ? " scale-110 active-pagination" : "bg-gray-300 hover:bg-gray-400 unactive-pagination"
                                }`}
                        />
                    ))}
                </div>
            )}
        </div >
    );
};

export default TodayMeetingsRow;
